<template>
  <div class="scholars">
    <!-- WHAT THE SCHOLARS LEARNED without lifting a flask (playtest 13:
         "number is light" turned up in the notebook with no idea when).
         Stops the board like the Frontier's encounters until it is read. -->
    <Teleport to="body">
      <div v-if="learnt" class="howto-backdrop encounter-backdrop" @click.self="dismissReveal">
        <div class="howto encounter encounter--reveal" role="dialog" aria-modal="true" aria-label="The Scholars learn">
          <div class="howto__body">
            <span class="encounter__kind">In the notebook<template v-if="revealsLeft > 1"> · {{ revealsLeft - 1 }} more</template></span>
            <h3 class="encounter__title">{{ learnt.said }}</h3>
            <p class="encounter__text">From {{ learnt.from }}. It is in ink in your notebook now.</p>
          </div>
          <div class="howto__nav">
            <span class="howto__dots" />
            <button class="primary howto__next" @click="dismissReveal">{{ revealsLeft > 1 ? 'Next' : 'Noted' }}</button>
          </div>
        </div>
      </div>
    </Teleport>
    <!--
      THE BENCH. Two principles go into the flasks, the glass says what they
      share. Matt on the Hall: "less like I'm pushing buttons on a computer" -
      the same rule here, so the experiment is an apparatus you load rather
      than a row of dropdowns.
    -->
    <!--
      The bench and the notebook are about the same size and you never need
      both at once, so they SHARE the screen and you swipe between them.
      Matt: "what if we had them share vertical space by swapping between
      them with a swipe... that would allow the whole thing to get on one
      screen." The tabs are the heading; the price of an experiment is a pip
      on the Mix button and is not repeated here.
    -->
    <!--
      House style rule 1: every board opens with its place and its purse.
      The Scholars used to open straight onto the Experiment/Notebook tabs,
      so this was the one board that never said how much day you had left.
    -->
    <div v-show="mode !== 'machines'" class="swap zone zone--loud">
      <div class="swap__tabs">
        <button data-tut="sch-experiment-tab" :class="{ 'swap__tab--on': page === 0 }" @click="go(0)">
          Experiment
          <span v-if="picked.length === 2" class="swap__flag">ready</span>
        </button>
        <button data-tut="sch-notebook-tab" :class="{ 'swap__tab--on': page === 1 }" @click="go(1)">
          Notebook
          <span class="swap__count">{{ solvedCount }}/{{ P.length }}</span>
        </button>
      </div>
      <span class="zone__count" :class="{ 'zone__count--spent': !civ.workers }">
        {{ civ.workers }} worker{{ civ.workers === 1 ? '' : 's' }} left
      </span>
    </div>
    <p v-if="!swipeSeen && mode !== 'machines'" class="swap__hint">‹ swipe to change card ›</p>
    <!-- The notebook is full: say what comes next (Andrew, playtest 13: "my
         entire notebook is full" - "what's the technology for kilns?"). -->
    <p v-if="nextStep && mode !== 'machines'" class="phone__note sch__next">{{ nextStep }}</p>



    <div
      v-show="mode !== 'machines'"
      class="pager"
      :style="paneHeight ? { height: `${paneHeight}px` } : null"
      @pointerdown="down"
      @pointermove="move"
      @pointerup="up"
      @pointercancel="up"
      @click.capture="swallow"
    >
      <div class="pager__track" :style="{ transform: `translateX(calc(${page * -50}% + ${dx}px))`, transition: dragging ? 'none' : null }">
        <!-- The card you are not on shows only once a SWIPE has begun
             (dragged, past 8px), never on a bare touch: un-hiding it on
             pointerdown read to iOS as a hover reveal, and Safari ate the
             tap - everything on the board needed a double tap (playtest
             2026-10-20, Andrew). -->
        <div ref="pane0" class="pager__pane" :class="{ 'pager__pane--away': page !== 0 && !dragged }">
          <div class="bench">
            <!-- A blink on arrival: the tabs say there are two cards, this
                 says you can swipe to the other one. It is centred on the
                 bench itself, which is the block that reads as the card. -->
            <span v-if="nudge && page === 0" class="nudge" aria-hidden="true">›</span>
      <div class="bench__slots" data-tut="sch-flasks">
        <button
          v-for="i in [0, 1]"
          :key="i"
          class="flask"
          :class="{ 'flask--full': picked[i], 'flask--next': canLook && !picked[i] && picked.length === i, 'flask--picking': picking === i }"
          :disabled="!canLook"
          @click="tapSlot(i)"
        >
          <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
          <svg class="flask__art" viewBox="0 0 24 24" aria-hidden="true" v-html="slotArt(i)" />
          <span class="flask__name">{{ picked[i] ? PRINCIPLE_NAMES[picked[i]] : 'empty' }}</span>
        </button>
        <span class="bench__plus" aria-hidden="true">+</span>
      </div>

      <!-- Tap an empty vial and the eight are right here, so the notebook
           is not the only way in. Matt: "I had forgotten that the notebook
           is where you select the elements." It stands in the glass's
           place, which keeps the card the same height. -->
      <!-- Both branches carry a key of their own, and they can never match:
           the compiler gives an unkeyed v-if branch a small number, the glass
           counted up from 0, and the first Mix made them both 1. In a
           production build Vue then took the picker for the glass and patched
           one into the other, and the bench fell apart (Sentry, an iPad,
           2026-10-12; the dev build never shows it). -->
      <div v-if="picking !== null" key="picker" class="picker">
        <span class="picker__lead">Into the flask:</span>
        <ul class="picker__grid">
          <li v-for="p in P" :key="p">
            <button
              class="picker__one"
              :class="{ 'picker__one--in': picked.includes(p) }"
              :disabled="picked.includes(p)"
              @click="fillSlot(p)"
            >
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
              <svg class="picker__art" viewBox="0 0 24 24" aria-hidden="true" v-html="artForPrinciple(p)" />
              <span>{{ PRINCIPLE_NAMES[p] }}</span>
            </button>
          </li>
        </ul>
      </div>

      <!-- The glass: the aim before you mix, the result after. -->
      <div
        v-else
        :key="`glass${glassKey}`"
        class="glass"
        data-tut="sch-glass"
        :class="{ 'glass--result': Boolean(result || stones), 'glass--eureka': result && result.eureka, 'glass--failed': result && result.failed, 'glass--nothing': result && !result.failed && !sharedCount(result) }"
      >
        <template v-if="stones">
          <span class="glass__what">the stones say</span>
          <span class="glass__badges">
            <span class="glass__both">{{ PRINCIPLE_NAMES[stones.principle] }} is</span>
            <Aspect :aspect="stones.aspect" :value="stones.value" />
          </span>
          <span class="glass__aim">for certain - it is in ink in the notebook now</span>
        </template>
        <template v-else-if="result && result.failed">
          <span class="glass__what">{{ PRINCIPLE_NAMES[result.a] }} + {{ PRINCIPLE_NAMES[result.b] }}</span>
          <strong class="glass__said">the reaction fails</strong>
        </template>
        <template v-else-if="result">
          <span class="glass__what">{{ PRINCIPLE_NAMES[result.a] }} + {{ PRINCIPLE_NAMES[result.b] }}</span>
          <strong v-if="!sharedCount(result)" class="glass__said">nothing in common</strong>
          <span v-else class="glass__badges">
            <span class="glass__both">both</span>
            <template v-for="(k, i) in sharedKeys(result)" :key="k">{{ joiner(i, sharedKeys(result).length) }}<Aspect :aspect="k" :value="result.shared[k]" /></template>
          </span>
          <template v-if="result.eureka">
            <span class="glass__aim">EUREKA · {{ techName(result.target) }} is yours</span>
            <span class="glass__does">{{ techText(result.target) }}</span>
          </template>
        </template>
        <!-- Aiming at a technology, the glass says so: what it needs, what
             it would do, and what is still missing. It used to take a second
             block under the chips, which grew the board (rule 7). -->
        <template v-else-if="target">
          <span class="glass__what">{{ techName(target) }} needs two that are both</span>
          <span class="glass__badges">
            <template v-for="(k, i) in needsOf(target)" :key="k.aspect">{{ joiner(i, needsOf(target).length) }}<Aspect :aspect="k.aspect" :value="k.value" /></template>
          </span>
          <span class="glass__does">{{ techText(target) }} · {{ techFor(target) }}</span>
          <span class="glass__aim">{{ picked.length === 2 ? 'mix them and find out' : 'fill both flasks' }}</span>
          <!-- Two you already KNOW fit: one tap (Seth, playtest 9: "once I've
               published theories that have been confirmed, I shouldn't have to
               go and find two that are dry and heavy - it should auto-fill"). -->
          <button v-if="fitPair && canLook && !samePair(fitPair)" class="linkish glass__fill" @click.stop="picked = [...fitPair]">
            Fill with {{ PRINCIPLE_NAMES[fitPair[0]] }} + {{ PRINCIPLE_NAMES[fitPair[1]] }} - your notebook says they fit
          </button>
        </template>
        <template v-else-if="picked.length === 2">
          <strong class="glass__said">ready</strong>
          <span class="glass__aim">the glass will say what these two share</span>
        </template>
        <template v-else>
          <strong class="glass__said">pick two principles to mix</strong>
        </template>
      </div>

      <!-- Aim it at a reaction. Chips, not a dropdown: each one shows what
           that technology needs, which is the clue you are reasoning from. -->
      <button
        class="action bench__go"
        data-tut="sch-mix"
        :class="{ 'action--off': !mixCheck.ok, 'action--preview': !canAct }"
        :disabled="!canAct || !mixCheck.ok"
        @click="doMix"
      >
        <!-- The price beside the name: one row, not two (one screen per board). -->
        <span class="action__top">
          <!-- Aimed, the button says so (Matt, playtest 10: "I'm not just
               mixing, I'm trying to create something"). -->
          <span class="action__label mix__label">{{ target ? `Mix for ${techName(target)}` : 'Mix' }} <ActionCost :cost="nextCost" /></span>
          <span class="action__gain">{{ target ? `${techName(target)}, or nothing` : 'what the two share' }}</span>
        </span>
        <!-- Why the price is what it is (playtest 9: "the mix cost climbs and
             the phone never says why"). -->
        <span class="action__blurb">{{ mixNote }}</span>
        <span v-if="!mixCheck.ok && picked.length === 2 && mixCheck.kind !== 'cost'" class="action__why">{{ mixCheck.why }}</span>
      </button>

      <div v-if="reachable.length" class="aims" data-tut="sch-research">
        <!-- Bench = reachable now; the tree below = everything (Matt, playtest 10). -->
        <span class="aims__label">Research · within reach now</span>
        <div class="aims__row">
          <!-- Never disabled: aiming costs nothing, and at dawn a chip that
               would not answer a tap read as broken (playtest KOymt). -->
          <button class="aim" :class="{ 'aim--on': !target }" @click="target = ''">
            <span class="aim__name">Experiment</span>
            <span class="aim__need">no technology</span>
          </button>
          <button
            v-for="t in reachable"
            :key="t.id"
            class="aim"
            :class="{ 'aim--on': target === t.id }"
            @click="aimAt(t.id)"
          >
            <span class="aim__name">{{ t.name }} <Pip kind="knowledge" :n="priceOf(t.id)" /></span>
            <span class="aim__need">
              both
              <Aspect v-for="k in needsOf(t.id)" :key="k.aspect" :aspect="k.aspect" :value="k.value" :word="false" />
              {{ t.reaction }}
            </span>
            <!-- What it does lives in the glass once it is aimed at - one
                 place per technology (playtest 11). -->
          </button>
        </div>

      </div>
    </div>

          <!-- Open until you fold it (playtest 9: "the tree started off hidden"). -->
          <details v-if="!wide" ref="treeFold" class="phone__log scholars__tree" :open="treeOpen" @toggle="onTreeToggle" @click="deferMeasure">
            <summary>The whole tree · {{ (civ.techs || []).length }} technolog{{ (civ.techs || []).length === 1 ? 'y' : 'ies' }} known</summary>
            <TechTree :civ="civ" :aimable="canAct" @aim="aimFromTree" />
          </details>
        </div>

        <div ref="pane1" class="pager__pane" :class="{ 'pager__pane--away': page !== 1 && !dragged }">

    <!--
      THE NOTEBOOK IS YOUR OWN SHEET (Matt, 2026-09-25, after Alchemists:
      "the game gives you the results, but the actual logic all has to happen
      on... a piece of paper with a grid... filling in that grid and keeping
      track of things manually is a lot of the fun"). The phone reports
      RESULTS - the experiments, the stones, settled theories - and never
      deduces. The grid is marked by hand: tap a square to cross it out,
      again to ring it, again to clear. Kept on this phone only.
    -->
    <div class="note-wrap">
      <span v-if="nudge && page === 1" class="nudge nudge--back" aria-hidden="true">‹</span>

      <!--
        THE RULES, on the sheet (Matt, 2026-09-25, mid-game: "the actual rules
        that govern the logic of combining things [must be] written out very
        clearly with the notebook... if they don't know the actual rules...
        they're going to be lost"). Open until you fold it; stays folded after.
      -->
      <details class="rules" :open="rulesOpen" @toggle="onRulesToggle">
        <summary>How mixing works - the rules</summary>
        <ol class="rules__list">
          <li><b>Every principle has a nature:</b> it is <b>hot or cold</b>, <b>wet or dry</b>, and <b>heavy or light</b> - one of each pair. That makes 8 possible natures: the 8 columns of your sheet.</li>
          <li><b>No two principles share a nature.</b> Each column is the answer for exactly one principle.</li>
          <li><b>Mix two principles and the glass names every property they SHARE.</b> Anything it does not name, they are opposite on.
            <ul>
              <li>"both dry" = both are dry, AND one is hot and the other cold, AND one heavy and the other light.</li>
              <li>"both dry and hot" = they share those two and are opposite on heavy/light.</li>
              <li>"nothing in common" = exact opposites on all three.</li>
            </ul>
          </li>
          <li><b>A technology needs two principles that are both what its badges say</b> (e.g. "both heavy"). Aim a mix at one: if the two share it, EUREKA and it is learned; if not, the glass says "not both heavy" - so at least one of them is not heavy.</li>
          <li><b>Consulting the stones</b> tells you one property of one principle for certain. It shows in ink beside its name.</li>
        </ol>
        <!-- The columns are drawn in these badges, so say which is which. -->
        <p class="rules__key">
          <span v-for="a in ASPECTS" :key="a" class="rules__pair">
            <Aspect :aspect="a" :value="true" /> <Aspect :aspect="a" :value="false" />
          </span>
        </p>
        <p class="rules__how"><b>Marking:</b> tap a square for ✕ (cannot be), again for ● (is), again to clear. Tap a principle's name to put it in a flask.</p>
        <p class="rules__how"><b>Working the sheet:</b> after a result, go along each of the two rows and ✕ every column that breaks the rule. Once a row has one column left, ● it - and ✕ that column in every other row.</p>
      </details>

      <!-- What the world has told you. Nothing here is worked out for you. -->
      <!-- Every experiment, scrolling inside its box; a principle in a flask
           lights up the lines it was in (Matt, playtest 10: "tap a principle and
           have them highlighted in the list"). -->
      <ul v-if="record.length" class="record record--open">
        <li v-for="(e, i) in record" :key="i" :class="{ 'record--eureka': e.eureka, 'record--hit': picked.includes(e.a) || picked.includes(e.b) }">
          <span class="record__pair">{{ PRINCIPLE_NAMES[e.a] }} + {{ PRINCIPLE_NAMES[e.b] }}</span>
          <span class="record__said">
            <template v-if="e.failed">not both {{ reactionFor(e.target) }}</template>
            <template v-else-if="!sharedCount(e)">nothing in common</template>
            <template v-else>
              both
              <template v-for="(k, j) in sharedKeys(e)" :key="k">{{ joiner(j, sharedKeys(e).length) }}<Aspect :aspect="k" :value="e.shared[k]" /></template>
            </template>
          </span>
          <!-- The other half of the reading (Matt, playtest 10: "fire and stone
               both hot - one is wet, one is dry, one is light, one is heavy"). -->
          <!-- Plain words (Matt, playtest 10: the glyph pairs read as "not equal"). -->
          <span v-if="!e.failed && restKeys(e).length" class="record__rest" :title="restOf(e)">differ: {{ restShort(e) }}</span>
          <span v-if="e.target" class="record__aim">{{ e.eureka ? `invented ${techName(e.target)}` : `not ${techName(e.target)}` }}</span>
        </li>
      </ul>
      <!-- THE RESULTS GRID: every pair, what the glass said (Matt, playtest
           10, from Alchemists: "a graphical way of showing you what the
           results of your experiments are" - a picture of the record, not
           the deduction sheet). Folded until wanted. -->
      <details v-if="experiments.length" class="results-fold" :open="resultsOpen" @toggle="resultsOpen = $event.target.open">
        <summary>Results grid · every pair you have mixed</summary>
        <div class="results" :style="{ '--n': P.length + 1 }">
          <span class="results__corner" />
          <span v-for="p in P" :key="`h${p}`" class="results__head" :title="PRINCIPLE_NAMES[p]">
            <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
            <svg viewBox="0 0 24 24" aria-hidden="true" v-html="artForPrinciple(p)" />
          </span>
          <template v-for="(a, i) in P" :key="`r${a}`">
            <span class="results__name" :title="PRINCIPLE_NAMES[a]">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
              <svg viewBox="0 0 24 24" aria-hidden="true" v-html="artForPrinciple(a)" />
            </span>
            <span v-for="(b, j) in P" :key="`c${a}${b}`" class="results__cell" :class="cellClass(a, b, i, j)" :title="cellTitle(a, b)">
              <template v-if="j > i && gridCell(a, b)">
                <template v-if="gridCell(a, b).failed">✕</template>
                <template v-else-if="!sharedCount(gridCell(a, b))">∅</template>
                <template v-else><Aspect v-for="k in sharedKeys(gridCell(a, b))" :key="k" :aspect="k" :value="gridCell(a, b).shared[k]" :word="false" /></template>
              </template>
            </span>
          </template>
        </div>
      </details>
      <!-- (No "no results yet" line: an empty record says it, and one
           screen per board has no room for a sentence about nothing.) -->

      <!-- Say what the grid IS (playtest 9: "this is a deduction sheet and I
           didn't understand what I was doing there"). -->
      <!-- Say what a TAP does (playtest 12, Matthew: "it's not really clear
           what it is I'm checking off"). -->
      <p class="sheet__lead">Deduction sheet. Tap a square: once ✕ it can't be, twice ● it is, again to clear. Tap a name to fill a flask.</p>
      <!-- The column heads are three glyphs apiece: say what the glyphs are
           (Matt, playtest 10: "the columns are really unclear... oh, hot wet heavy"). -->
      <p class="sheet__key rules__key">
        <span class="sheet__key-lead">Each column is one nature, read down:</span>
        <span v-for="a in ASPECTS" :key="a" class="rules__pair">
          <Aspect :aspect="a" :value="true" /> <Aspect :aspect="a" :value="false" />
        </span>
      </p>
      <div class="sheet" data-tut="sch-sheet" :class="{ 'note--asking': needsPick }">
        <div class="sheet__head">
          <span class="sheet__name sheet__key">✕ can't be<br>● is</span>
          <span v-for="k in NATURE_KEYS" :key="k" class="sheet__nature">
            <Aspect v-for="a in ASPECTS" :key="a" :aspect="a" :value="natureOfKey(k)[a]" :word="false" />
          </span>
        </div>
        <div v-for="p in P" :key="p" class="sheet__row" :class="{ 'sheet__row--picked': picked.includes(p), 'sheet__row--settled': settled[p] }">
          <button class="sheet__name" :disabled="!canLook" :title="`Put ${PRINCIPLE_NAMES[p]} in a flask`" @click="pick(p)">
            <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
            <svg class="note__emblem" viewBox="0 0 24 24" aria-hidden="true" v-html="artForPrinciple(p)" />
            <span class="note__title">{{ PRINCIPLE_NAMES[p] }}</span>
            <!-- Told outright (the stones, a find, a settled theory): results, in ink. -->
            <span class="sheet__told">
              <Aspect v-for="a in ASPECTS.filter((x) => markOf(p, x).known)" :key="a" :aspect="a" :value="markOf(p, a).value" tone="ink" :word="false" />
            </span>
          </button>
          <button
            v-for="k in NATURE_KEYS"
            :key="k"
            class="sheet__cell"
            :class="`sheet__cell--${sheetMark(p, k) || 'open'}`"
            :aria-label="`${PRINCIPLE_NAMES[p]} as ${k}: ${sheetMark(p, k) || 'unmarked'}`"
            @click="cycleMark(p, k)"
          >{{ sheetMark(p, k) === 'out' ? '✕' : sheetMark(p, k) === 'in' ? '●' : '' }}</button>
        </div>
      </div>
    </div>
        </div>
      </div>
    </div>

    <!-- On a tablet both cards sit side by side, so the tree gets the
         board's full width under them instead of half (playtest EG-Uh:
         "the tree feels really squished on the left"). -->
    <details v-if="wide && mode !== 'machines'" class="phone__log scholars__tree" :open="treeOpen" @toggle="onTreeToggle">
      <summary><span class="tree__door">{{ treeOpen ? '▾' : '▸' }} Open the whole tree</span> · {{ (civ.techs || []).length }} technolog{{ (civ.techs || []).length === 1 ? 'y' : 'ies' }} known</summary>
      <TechTree :civ="civ" :aimable="canAct" @aim="aimFromTree" />
    </details>

    <!-- ============ publishing a theory: principle, nature, who it teaches -->
    <template v-if="mode === 'publish'">
      <template v-if="!theoryOf">
        <p class="counter-band">Publish a theory about which principle?</p>
        <ul class="hall__actions hall__actions--fit">
          <li v-for="p in openPrinciples" :key="p.id">
            <button class="tile" :disabled="!p.ok" @click="startTheory(p.id)">
              <span class="tile__head">
                <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
                <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="artForPrinciple(p.id)" />
                <span class="tile__name">{{ PRINCIPLE_NAMES[p.id] }}</span>
              </span>
              <span class="tile__text">{{ p.note }}</span>
            </button>
          </li>
        </ul>
      </template>

      <template v-else>
        <p class="counter-band">{{ PRINCIPLE_NAMES[theoryOf] }} is…</p>
        <div class="theory">
          <div v-for="a in ASPECTS" :key="a" class="mode-switch" role="group" :aria-label="a">
            <button :class="{ 'mode-switch--on': theory[a] }" @click="theory[a] = true">
              {{ ASPECT_WORDS[a].true }}
              <small>{{ hintFor(theoryOf, a, true) }}</small>
            </button>
            <button :class="{ 'mode-switch--on': theory[a] === false }" @click="theory[a] = false">
              {{ ASPECT_WORDS[a].false }}
              <small>{{ hintFor(theoryOf, a, false) }}</small>
            </button>
          </div>
        </div>

        <h4 class="zone">
          <span class="zone__label">If it holds, teach</span>
        </h4>
        <!-- Quiet tiles, the chosen one in its role's colour: Publish below is
             the one gold thing (Matt, playtest 10: "it's all yellow... I can't
             focus on any of it"). -->
        <ul class="hall__actions hall__actions--fit theory-teach">
          <li v-for="r in ROLES" :key="r" :style="{ '--role': ROLE_INFO[r].colour }">
            <button class="tile" :class="{ 'tile--on': teach === r }" @click="teach = r">
              <span class="tile__head"><span class="tile__name">the {{ ROLE_INFO[r].label }}</span></span>
              <span class="tile__text">+1 worker at the next dawn</span>
            </button>
          </li>
        </ul>

        <!-- The special button, looking it (Seth, playtest 9: "it just looks
             like all the other buttons but it's the special one"). -->
        <button
          class="action action--go"
          :class="{ 'action--off': !publishCheck.ok }"
          :disabled="!canAct || !publishCheck.ok"
          @click="act('publish', { principle: theoryOf, nature: { ...theory }, teach })"
        >
          <span class="action__top">
            <span class="action__label">Publish</span>
            <span class="action__gain">the night judges it</span>
          </span>
          <ActionCost :cost="PUBLISH_COST" />
          <span class="action__blurb">Right: settled, and 2 knowledge back. Wrong: debunked, and −1 happiness.</span>
          <span v-if="!publishCheck.ok && publishCheck.kind !== 'cost'" class="action__why">{{ publishCheck.why }}</span>
        </button>
      </template>
      <button class="secondary" @click="close">Back</button>
    </template>

    <!-- ============ consulting the stones: which principle? -->
    <template v-else-if="mode === 'hint'">
      <p class="counter-band">Consult the stones about which principle?</p>
      <ul class="hall__actions hall__actions--fit">
        <li v-for="p in stoneTargets" :key="p.id">
          <button class="tile" :disabled="!p.ok" @click="act('hint', { principle: p.id })">
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="artForPrinciple(p.id)" />
              <span class="tile__name">{{ PRINCIPLE_NAMES[p.id] }}</span>
            </span>
            <span class="tile__text">{{ p.note }}</span>
          </button>
        </li>
      </ul>
      <button class="secondary" @click="close">Back</button>
    </template>

    <!-- ============ the machines (the ladder, 2026-10-15) -->
    <template v-else-if="mode === 'machines'">
      <MachinesPanel :game="g" :civ="civ" :can-act="canAct" :can-look="canLook" :aim="machineAim" @act="(e) => $emit('act', e)" @notebook="close(); go(1)" />
      <button class="secondary" @click="close">Back</button>
    </template>

    <!-- ============ the rest of the day -->
    <template v-else>
      <!-- Where the puzzle is going (Matt, after playtest 9: publishing is
           the end game of the deduction, and it opens the next phase) - on
           the heading's own line (one screen per board). -->
      <h4 class="zone">
        <span class="zone__label">Things to do here</span>
      </h4>
      <ul class="hall__actions hall__actions--fit" data-tut="sch-more">
        <li v-for="a in buttons" :key="a.id">
          <button
            class="tile"
            :class="{ 'tile--off': !a.check.ok || a.off }"
            :data-tut="a.id === 'fire' ? 'sch-machines' : null"
            :disabled="!canAct || !a.check.ok"
            @click="tap(a)"
          >
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="a.art" />
              <span class="tile__name">{{ a.label }}</span>
            </span>
            <ActionCost :cost="a.cost" />
            <span class="tile__gain">{{ a.check.ok || a.check.kind === 'cost' ? a.gain : a.check.why }}</span>
          </button>
        </li>
      </ul>
    </template>

    <MixReveal v-if="reveal" :exp="reveal" :tech-name="techName(reveal.target)" :need="reactionFor(reveal.target)" :does="reveal.eureka ? techText(reveal.target) : ''" :opens="reveal.eureka ? opensAfter(reveal.target) : []" @close="reveal = null" />
  </div>
</template>

<script>
import {
  actionsForRole, checkAction, resolveCost, resolveLabel, mixesPerDay, mixCost, availableTechs,
  techPrice,
} from '@/engine/roleActions';
import { civFor } from '@/engine/game';
import { TECHS, techForText } from '@/engine/cards';
import {
  ROLES, ROLE_INFO, PUBLISH_COST,
} from '@/engine/constants';
import {
  PRINCIPLE_NAMES, ASPECTS, ASPECT_WORDS, NATURES, natureKey, loreOf, consistentNatures,
  deducedAspects, describeShared, describeReaction, aspectWord, whyDeduced, joinWords,
  PRINCIPLES,
} from '@/engine/lore';
import { onBench } from '@/engine/machines';
import { canAfford as affords } from '@/engine/effects';
import { artForPrinciple, artForScholarAction, FLASK_ART } from './loreArt';
import TechTree from './TechTree.vue';

const WIDE = '(min-width: 760px)';
import ActionCost from './ActionCost.vue';
import Pip from './Pip.vue';
import Aspect from './Aspect.vue';
import MixReveal from './MixReveal.vue';
import MachinesPanel from './MachinesPanel.vue';

const SWIPE_KEY = 'commonwealth:scholars-swipe';

// The Scholars' board: the bench (two flasks and the glass), the notebook
// (ink for what is known, pencil for what follows), the record, and the
// other ways to spend a day. Only this phone shows the notebook, and
// `civ.lore.natures` - the answer key - is never rendered anywhere.
const REVEAL_KEY = 'commonwealth:scholar-reveals';

export default {
  name: 'ScholarsBoard',
  components: {
    Pip,
    ActionCost, TechTree, Aspect, MixReveal, MachinesPanel },
  props: {
    game: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    preview: { type: Boolean, default: false },
  },
  emits: ['act'],
  data() {
    return {
      TECHS,
      sheet: {},
      // Folded until this phone opens it, like the tree (one screen per
      // board: open, the rules are ~500px; the tutorial teaches them).
      rulesOpen: (() => { try { return localStorage.getItem('commonwealth:rules-folded') === '0'; } catch { return false; } })(),
      resultsOpen: false,
      // Folded until this phone opens it (one screen per board: open, the
      // tree alone is ~480px; the bench's Research row already lists what
      // can be aimed at). Remembered once opened or folded again.
      treeOpen: (() => { try { return localStorage.getItem('commonwealth:tree-folded') === '0'; } catch { return false; } })(),
      // A tablet (game.scss's 760px): both cards show at once.
      wide: Boolean(window.matchMedia?.(WIDE).matches),
      picked: [],
      target: '',
      mode: null,
      theoryOf: null,
      // Log index up to which reveals have been read on this phone.
      revealedTo: -1,
      revealedIn: 0,
      theory: { heat: true, moisture: true, weight: true },
      teach: 'trade',
      // The glass keeps the last result up until the next pair goes in.
      showResult: false,
      glassKey: 0,
      // The experiment being revealed (MixReveal).
      reveal: null,
      // A deep technology tapped on the tree opens the machines on it.
      machineAim: '',
      // How the notebook knows, and which squares it filled in last.
      showWhy: false,
      wasSure: null,
      fresh: [],
      reasons: [],
      crossedOff: '',
      // The two cards that share the screen: 0 the bench, 1 the notebook.
      // `?notebook=1` in dev opens on the notebook, for the screenshot loop.
      page: (import.meta.env.DEV && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('notebook') === '1') ? 1 : 0,
      picking: null,
      nudge: true,
      paneHeight: 0,
      burst: 0,
      burstAt: 0,
      dx: 0,
      dragging: false,
      dragged: false,
      swipeSeen: true,
      stones: null,
      // (There was a second bench of eight; the machines replaced it.)
      set: 'base',
      // What the notebook held last time we looked, so a new certainty can
      // be announced instead of quietly appearing as a bold word.
      knownSeen: {},
      NATURES,
      // Columns in a Gray order: each neighbour differs by ONE trait, so a
      // ruled-out trait wipes a run of four side by side (Matt, playtest 10,
      // after Alchemists' sheet: "arranged in a very clever way").
      NATURE_KEYS: ['hwH', 'hwL', 'hdL', 'hdH', 'cdH', 'cdL', 'cwL', 'cwH'].filter((k) => NATURES.some((n) => natureKey(n) === k)),
      PRINCIPLE_NAMES,
      ASPECTS,
      ASPECT_WORDS,
      ROLES,
      ROLE_INFO,
      PUBLISH_COST,
      describeShared,
      artForPrinciple,
    };
  },
  computed: {
    // Practice lives in the tutorial now (src/tutorial): the whole game is
    // the copy, so the board just reads it.
    g() { return this.game; },
    civ() { return civFor(this.g, 'science'); },
    fullLore() { return loreOf(this.civ) || { experiments: [], known: {}, settled: {}, published: {}, reactions: {} }; },
    // This bench's book: experiments, ink, theories. Reactions are one table.
    lore() { return this.fullLore; },
    reactions() { return this.fullLore.reactions || {}; },
    P() { return PRINCIPLES; },
    experimentCount() { return (this.fullLore.experiments || []).length; },
    canAct() { return !this.readonly && !this.preview; },
    // At dawn the board is a preview: nothing can be DONE, but you can still
    // load the flasks and aim, to plan what the workers are for (playtest 9:
    // "maybe you were trying to do things that were blocked unnecessarily").
    canLook() { return !this.readonly; },
    perDay() { return mixesPerDay(this.civ); },
    // The WHOLE price, the good and (aimed) the knowledge too: it showed the
    // workers alone, so a Mix greyed for want of a good gave no reason at
    // all (playtest cizOM: "I have selected two elements and I can't tap mix").
    nextCost() {
      const a = actionsForRole('science').find((x) => x.id === 'mix');
      return a ? resolveCost(a, this.civ, 1, { target: this.target || undefined }, this.g) : mixCost(this.g);
    },
    // Two elements the notebook already pins down (a held theory, being
    // told all three, or your own single ●) that share what the aimed
    // technology needs.
    fitPair() {
      if (!this.target) return null;
      const needs = this.needsOf(this.target);
      if (!needs.length) return null;
      const fits = this.P.filter((p) => {
        const n = this.pinnedNature(p);
        return n && needs.every((k) => n[k.aspect] === k.value);
      });
      return fits.length >= 2 ? fits.slice(0, 2) : null;
    },
    mixNote() {
      const done = this.g.taken?.science?.mix || 0;
      const cheap = mixesPerDay(this.civ);
      const left = cheap - done;
      // One line (one screen per board): the price now, and that it climbs.
      if (left > 0) return `${left} more at 1 worker today, then each costs one more`;
      return 'Each mix today costs one more worker than the last';
    },
    canAfford() { return this.civ.workers >= this.nextCost.workers; },
    possible() { return consistentNatures(this.fullLore); },
    deduced() { return deducedAspects(this.fullLore); },
    // Every square the notebook is now sure of, as "fire.heat" keys.
    sureKeys() {
      const out = [];
      this.P.forEach((p) => ASPECTS.forEach((a) => {
        if (this.deduced[p]?.[a] !== undefined) out.push(`${p}.${a}`);
      }));
      return out;
    },

    settled() { return this.lore.settled || {}; },
    knownKey() { return JSON.stringify(this.fullLore.known || {}); },
    solvedCount() { return this.P.filter((p) => this.possible.candidates[p].length === 1).length; },
    // All eight natures known and no machine to climb to yet: the next rung, in words.
    nextStep() {
      if (this.solvedCount < this.P.length) return null;
      if ((this.civ.city?.buildings || []).some((b) => b.type === 'kiln' && !b.burned)) return null;
      const kilns = TECHS.kilns;
      if (!kilns) return null;
      const has = (this.civ.techs || []).includes('kilns');
      const needs = (kilns.needs || []).filter((id) => !(this.civ.techs || []).includes(id)).map((id) => TECHS[id]?.name || id);
      return has
        ? 'All eight natures are known. Next: ask the Stewards to build a Kiln - every tier 3 technology is made from what it fires.'
        : `All eight natures are known. Next: learn ${kilns.name}${needs.length ? ` (after ${needs.join(' and ')})` : ''}, then the Stewards build a Kiln and the next technologies come from what it fires.`;
    },
    pendingReveals() {
      if (!this.canAct) return [];
      const out = [];
      const base = this.g.logBase || 0;
      (this.g.log || []).forEach((l, k) => {
        const i = base + k;
        if (i <= this.revealedTo || !l.reveals?.length) return;
        // Your own mix says what it found on its own card.
        if (l.kind === 'act' && l.role === 'science') return;
        l.reveals.forEach((r, j) => { if (i > this.revealedTo + 1 || j >= this.revealedIn) out.push({ ...r, at: i, j }); });
      });
      return out;
    },
    learnt() { return this.pendingReveals[0] || null; },
    revealsLeft() { return this.pendingReveals.length; },
    /** Everything on either card that can change its height. */
    cardSig() {
      return [this.picked.length, this.target, this.picking, this.experiments.length, this.set,
        this.crossedOff, Boolean(this.stones), this.record.length].join('|');
    },
    ruledOutCount() {
      const p = this.picked[0];
      return p ? NATURES.length - this.possible.candidates[p].length : 0;
    },
    // The bench is waiting on you: the notebook is the thing to tap, so it
    // is allowed to look like the live choice.
    needsPick() { return this.canAct && this.picked.length < 2 && this.canAfford; },
    published() { return this.lore.published || {}; },
    experiments() { return this.lore.experiments || []; },
    record() { return [...this.experiments].reverse(); },
    // What the glass is showing: the last experiment, until a new pair is picked.
    result() { return this.showResult ? this.experiments[this.experiments.length - 1] || null : null; },
    // This bench's technologies within reach; the other bench's are counted.
    reachable() {
      return availableTechs(this.civ)
        .filter((id) => onBench(TECHS[id].tier))
        .map((id) => ({
          id, name: TECHS[id].name, reaction: describeReaction(this.reactions[id] || {}),
        }));
    },
    mixCheck() {
      const [a, b] = this.picked;
      return checkAction(this.g, 'science', 'mix', { a, b, target: this.target || undefined });
    },
    publishCheck() {
      if (ASPECTS.some((a) => typeof this.theory[a] !== 'boolean')) return { ok: false, why: 'Pick all three first', kind: 'rule' };
      return checkAction(this.g, 'science', 'publish', { principle: this.theoryOf, nature: { ...this.theory } });
    },
    // Every principle with a note on whether there is anything left to say.
    openPrinciples() {
      return this.P.map((id) => {
        let note = 'open';
        if (this.settled[id]) note = 'settled';
        else if (this.published[id]) note = 'waiting on the night';
        return { id, ok: !this.settled[id] && !this.published[id], note };
      });
    },
    stoneTargets() {
      return this.P.map((id) => {
        const open = ASPECTS.filter((a) => this.lore.known?.[id]?.[a] === undefined);
        return { id, ok: open.length > 0, note: open.length ? `${open.length} of 3 unknown` : 'all known' };
      });
    },
    buttons() {
      const { civ } = this;
      return actionsForRole('science')
        .filter((a) => a.id !== 'mix' && a.id !== 'make')
        .filter((a) => a.id !== 'build-monument' || civ.monument.revealed)
        // The Bronze Age's survey, from the Bronze Age (it is not a tile before).
        .filter((a) => a.id !== 'survey' || (civ.age || 0) >= 1)
        .map((a) => ({
          id: a.id,
          label: a.id === 'hint' ? 'Consult the stones' : (a.id === 'fire' ? 'Machines' : resolveLabel(a, civ)),
          gain: a.gain(civ),
          cost: resolveCost(a, civ),
          art: artForScholarAction(a.id),
          // The machines tile opens the panel whether or not a machine can
          // fire, so the way up the tree is always findable.
          check: a.id === 'fire' ? { ok: true } : checkAction(this.g, 'science', a.id, this.paramsFor(a.id)),
          // ...but it wears the gold edge only when one could fire now
          // (playtest y2Qtg: "it seems to think I can do this thing").
          off: a.id === 'fire' && !(a.legal(this.g) && affords(civ, resolveCost(a, civ))),
        }));
    },
  },
  watch: {
    // Which squares the last thing you did filled in. Kept so the ? can
    // explain THAT, rather than everything the notebook has ever known.
    sureKeys: {
      immediate: true,
      handler(now) {
        const was = this.wasSure;
        this.wasSure = now;
        if (!was) return;
        const added = now.filter((k) => !was.includes(k));
        if (added.length) this.fresh = added;
      },
    },
    // A result arrives in the glass and stays there. Matt's standing rule:
    // the payoff moment gets a beat of its own.
    experimentCount(now, was) {
      if (now > was) {
        this.showResult = true;
        this.glassKey += 1;
        // ...and its moment, on top.
        if (this.canAct) this.reveal = (this.fullLore.experiments || []).at(-1) || null;
      }
    },
    page() { this.$nextTick(this.measureNow); },
    // Everything that changes how tall a card is. A ResizeObserver alone
    // was not enough: a tab that is not painting delivers neither resize
    // callbacks nor animation frames, so the card stayed clipped at its
    // old height until something else nudged it.
    cardSig() { this.$nextTick(this.measureNow); },
    // Consulting the stones used to say nothing at all on the phone - the
    // answer went to the log, which is on the TV. Matt: "I don't know what
    // that just told me." Now the glass says it, like any other result.
    knownKey() {
      const fresh = this.newlyKnown();
      this.knownSeen = this.snapshotKnown();
      if (!fresh) return;
      this.stones = fresh;
      this.showResult = false;
      this.glassKey += 1;
    },
  },
  created() {
    this.knownSeen = this.snapshotKnown();
    // Reveals read before a reload stay read; a phone new to this game
    // starts from where the log is now (like the Frontier's notices).
    let stored = null;
    try { stored = localStorage.getItem(`${REVEAL_KEY}:${this.game.seed}`); } catch { stored = null; }
    this.revealedTo = stored !== null ? Number(stored) : ((this.game.logBase || 0) + (this.game.log || []).length) - 1;
    try { this.swipeSeen = Boolean(localStorage.getItem(SWIPE_KEY)); } catch { this.swipeSeen = true; }
  },
  mounted() {
    this.loadSheet();
    this.wideQuery = window.matchMedia?.(WIDE);
    this.onWide = (e) => { this.wide = e.matches; };
    this.wideQuery?.addEventListener?.('change', this.onWide);
    // Blink the arrow on arrival, then leave the screen alone.
    this.nudgeTimer = setTimeout(() => { this.nudge = false; }, 3400);
    this.$nextTick(this.measureNow);
    // The cards change height as you pick, mix and open things.
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.measure());
      [this.$refs.pane0, this.$refs.pane1].forEach((el) => el && this.ro.observe(el));
    }
  },
  beforeUnmount() {
    this.wideQuery?.removeEventListener?.('change', this.onWide);
    if (this.ro) this.ro.disconnect();
    if (this.nudgeTimer) clearTimeout(this.nudgeTimer);
  },
  methods: {
    sharedCount(e) { return Object.keys(e?.shared || {}).length; },
    // What the two differ on, spelled out: "one wet, one dry · one heavy, one light".
    restKeys(e) { return ASPECTS.filter((k) => !(k in (e?.shared || {}))); },
    // The results grid: the latest reading for a pair, either way round.
    gridCell(a, b) {
      for (let i = this.experiments.length - 1; i >= 0; i -= 1) {
        const e = this.experiments[i];
        if ((e.a === a && e.b === b) || (e.a === b && e.b === a)) return e;
      }
      return null;
    },
    cellClass(a, b, i, j) {
      if (j <= i) return 'results__cell--blank';
      const e = this.gridCell(a, b);
      if (!e) return 'results__cell--open';
      return { 'results__cell--eureka': e.eureka, 'results__cell--none': !e.failed && !this.sharedCount(e), 'results__cell--hit': this.picked.includes(a) || this.picked.includes(b) };
    },
    cellTitle(a, b) {
      const e = this.gridCell(a, b);
      if (!e) return `${PRINCIPLE_NAMES[a]} + ${PRINCIPLE_NAMES[b]}: not mixed yet`;
      if (e.failed) return `${PRINCIPLE_NAMES[a]} + ${PRINCIPLE_NAMES[b]}: not both ${this.reactionFor(e.target)}`;
      return `${PRINCIPLE_NAMES[a]} + ${PRINCIPLE_NAMES[b]}: ${this.sharedCount(e) ? `both ${this.sharedKeys(e).map((k) => aspectWord(k, e.shared[k])).join(' and ')}` : 'nothing in common'}`;
    },
    restShort(e) { return this.restKeys(e).map((k) => `${aspectWord(k, true)}/${aspectWord(k, false)}`).join(', '); },
    restOf(e) {
      const rest = this.restKeys(e);
      if (!rest.length) return '';
      const pair = (k) => `one ${aspectWord(k, true)}, one ${aspectWord(k, false)}`;
      return (this.sharedCount(e) ? '' : 'opposites: ') + rest.map(pair).join(' · ');
    },
    // Eureka: what this technology lets the tree reach next.
    opensAfter(id) {
      if (!id) return [];
      const known = new Set([...(this.civ.techs || []), id]);
      return Object.values(TECHS).filter((t) => t.id !== id && !known.has(t.id) && (t.needs || []).includes(id) && (t.needs || []).every((n) => known.has(n))).map((t) => t.name);
    },
    sharedKeys(e) { return ASPECTS.filter((k) => k in (e?.shared || {})); },
    // "cold and wet", "cold, wet, and heavy" - the word that goes BEFORE
    // the badge at this position.
    joiner(i, n) {
      if (i === 0) return '';
      if (n === 2) return ' and ';
      return i === n - 1 ? ', and ' : ', ';
    },
    /** The one property the world just let slip, if any. */
    newlyKnown() {
      const now = this.fullLore.known || {};
      const seen = this.knownSeen || {};
      for (const p of PRINCIPLES) {
        for (const a of ASPECTS) {
          if (now[p]?.[a] !== undefined && seen[p]?.[a] === undefined) {
            return { principle: p, aspect: a, value: now[p][a] };
          }
        }
      }
      return null;
    },
    snapshotKnown() {
      return JSON.parse(JSON.stringify(this.fullLore.known || {}));
    },
    onTreeToggle(e) {
      this.treeOpen = e.target.open;
      try { localStorage.setItem('commonwealth:tree-folded', e.target.open ? '0' : '1'); } catch { /* storage off */ }
      this.deferMeasure();
    },
    // Aiming puts the technology in the glass - what it needs and what it
    // does. The last result (or the open picker) used to win the glass, so
    // after a mix a tapped technology said nothing (playtest 2026-10-20,
    // Andrew: "where does it tell me what it does").
    aimAt(id) {
      this.target = id;
      this.showResult = false;
      this.stones = null;
      this.picking = null;
    },
    // "Research Tracking" on the tree: the bench, aimed at it, at the top.
    aimFromTree(id) {
      // Above tier 2, a technology is made in a machine: open them on it.
      if (!onBench(TECHS[id].tier)) {
        this.machineAim = id;
        this.mode = 'machines';
        return;
      }
      this.aimAt(id);
      this.go(0);
      // Show the pick on the bench (Matt, playtest 10: "scroll to put that
      // research visible... not always clear that it selected it").
      this.$nextTick(() => this.$el.querySelector('.aims__row .aim--on')?.scrollIntoView?.({ inline: 'center', block: 'nearest', behavior: 'smooth' }));
      this.$nextTick(() => this.$el?.querySelector?.('.glass')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }));
    },
    // A technology's reaction as badges: "needs [wet] [light]".
    pinnedNature(p) {
      if (this.settled[p]) return this.settled[p];
      const told = this.lore.known?.[p] || {};
      if (ASPECTS.every((a) => typeof told[a] === 'boolean')) return told;
      const ringed = this.NATURE_KEYS.filter((key) => this.sheetMark(p, key) === 'in');
      return ringed.length === 1 ? this.natureOfKey(ringed[0]) : null;
    },
    samePair(pair) { return this.picked.length === 2 && pair.every((p) => this.picked.includes(p)); },
    needsOf(id) {
      const need = this.reactions[id] || {};
      return ASPECTS.filter((k) => k in need).map((k) => ({ aspect: k, value: need[k] }));
    },
    markOf(p, a) {
      if (this.settled[p]) return { known: true, deduced: false, value: this.settled[p][a] };
      const k = this.lore.known?.[p]?.[a];
      if (k !== undefined) return { known: true, deduced: false, value: k };
      const d = this.deduced[p]?.[a];
      if (d !== undefined) return { known: false, deduced: true, value: d };
      return { known: false, deduced: false, value: false };
    },
    // ---- the two cards
    go(to) {
      if (to === this.page) return;
      // Matt: "when swiping, let's automatically first close any
      // collapsible panes." The three thin ones fold; the technology you
      // opened under Research stays, since that is what you are working on.
      this.fold();
      // A swipe whose finger lifted outside the pager never heard its
      // pointerup, so its offset stayed on the track: the tab said
      // Notebook and the bench stayed on screen (playtest 7).
      this.dragging = false;
      this.dx = 0;
      this.page = to;
      if (!this.swipeSeen) {
        this.swipeSeen = true;
        try { localStorage.setItem(SWIPE_KEY, '1'); } catch { /* storage off */ }
      }
    },
    // The pager is exactly as tall as the card you are looking at, so the
    // shorter one does not leave a hole under it.
    //
    // Measuring in a ResizeObserver and writing the result back into the
    // layout is a loop waiting to happen - setting the height can bring the
    // page's scrollbar in or out, which rewraps the card, which resizes it
    // again. It froze the tab once. So: one measurement per frame, ignore
    // changes under 2px, and if the height starts flapping between two
    // values, keep the larger and stop.
    // Working out WHY runs the solver once per clue, so it happens when you
    // ask and not a moment before - a computed would redo it on every
    // keystroke of the game and lock the phone up.
    askWhy() {
      this.showWhy = !this.showWhy;
      if (!this.showWhy) return;
      const keys = this.fresh.length ? this.fresh : this.sureKeys;
      this.reasons = keys.slice(0, 3).map((key) => {
        const [p, a] = key.split('.');
        const why = whyDeduced(this.fullLore, p, a);
        if (!why) return null;
        return {
          key,
          fact: `${PRINCIPLE_NAMES[p]} is ${aspectWord(a, why.value)}`,
          told: why.told,
          because: why.needed.length ? joinWords(why.needed) : '',
        };
      }).filter(Boolean);
      this.deferMeasure();
    },
    // A fold opening inside a pager of fixed height was clipped to nothing:
    // Brian opened the tree and saw an empty panel (playtest 7). Let the
    // pager go to its natural height first, then measure it again.
    deferMeasure() {
      this.paneHeight = 0;
      this.burst = 0;
      this.$nextTick(this.measureNow);
    },
    fold() {
      ['treeFold', 'naturesFold', 'recordFold'].forEach((r) => {
        const el = this.$refs[r];
        if (el) el.open = false;
      });
    },
    // The height is the TALLER of the two cards, not the one you are on:
    // Matt, on swiping between cards of different heights, "it kind of
    // makes the content below it a little jumpy... they need to maintain
    // the same height no matter what is expanded in each of them".
    measureNow() {
      // The card on show sets the height, not the taller of the two: the
      // notebook is long, and the bench sat over a screen of empty space.
      const el = this.page === 1 ? this.$refs.pane1 : this.$refs.pane0;
      if (!el) return;
      const h = el.offsetHeight;
      if (Math.abs(h - this.paneHeight) <= 2) return;
      // A real loop measures dozens of times a second; folding a panel
      // measures twice. So count them in a window rather than treating any
      // there-and-back as a loop - that guard left the board stuck at the
      // height of a panel you had just closed.
      const now = Date.now();
      if (now - this.burstAt > 400) { this.burstAt = now; this.burst = 0; }
      this.burst += 1;
      if (this.burst > 12) { this.paneHeight = Math.max(h, this.paneHeight); return; }
      this.paneHeight = h;
    },
    // The observer measures straight away too: throttling this to a frame
    // meant a card that grew while the tab was in the background - no
    // frames run there - stayed clipped at its old height. The burst
    // counter in measureNow is what stops a runaway, not the throttle.
    measure() { this.measureNow(); },
    tapSlot(i) {
      if (!this.canLook) return;
      if (this.picked[i]) { this.unpick(i); return; }
      this.picking = this.picking === i ? null : i;
    },
    fillSlot(p) {
      this.pick(p);
      this.picking = null;
    },
    down(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Hear the release even if the finger leaves the pager.
      window.addEventListener('pointerup', this.up, { once: true });
      window.addEventListener('pointercancel', this.up, { once: true });
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.dragging = true;
      this.dragged = false;
      this.dx = 0;
    },
    move(e) {
      if (!this.dragging) return;
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      // A vertical drag belongs to the page, not to the cards.
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { this.dragging = false; this.dx = 0; return; }
      // Don't drag past the ends.
      this.dx = (this.page === 0 && dx > 0) || (this.page === 1 && dx < 0) ? dx * 0.25 : dx;
      if (Math.abs(dx) > 8) this.dragged = true;
    },
    up() {
      if (!this.dragging) return;
      const { dx } = this;
      this.dragging = false;
      this.dx = 0;
      if (Math.abs(dx) > 40) this.go(dx < 0 ? 1 : 0);
      // The click the browser fires after this pointerup is the one to
      // swallow - and only that one. Held any longer, the flag ate the NEXT
      // real tap instead, so a swipe to the notebook made the first row you
      // touched do nothing.
      if (this.dragged) setTimeout(() => { this.dragged = false; }, 0);
    },
    // A drag is never a tap - the same eight pixels the table's world uses.
    swallow(e) {
      if (!this.dragged) return;
      e.stopPropagation();
      e.preventDefault();
    },
    techName(id) { return TECHS[id]?.name || ''; },
    techText(id) { return TECHS[id]?.text || ''; },
    techFor(id) {
      return techForText(id, (r) => ROLE_INFO[r].label);
    },
    keyOf(n) { return natureKey(n); },
    onRulesToggle(e) {
      this.rulesOpen = e.target.open;
      try { localStorage.setItem('commonwealth:rules-folded', e.target.open ? '0' : '1'); } catch { /* storage off */ }
      this.deferMeasure();
    },
    natureOfKey(k) { return NATURES.find((n) => natureKey(n) === k) || {}; },
    // The hand-kept sheet, per game and per bench, on this phone.
    // Per GAME (its seed) and per bench. It used meta.seed, which does not
    // exist, so every game shared one sheet; the old key is read once and
    // carried over so nobody loses marks mid-game.
    sheetKey() { return `commonwealth:sheet:${this.game.seed ?? 'x'}:${this.set}`; },
    oldSheetKey() { return `commonwealth:sheet:[object Object]:${this.set}`; },
    // A theory the night held up is a DISCOVERY: its row is filled in for
    // you, in blue, and locked (Seth, playtest 9: "it should be more obvious
    // I've discovered it, make it blue"; Matt: "lock you from modifying the
    // scratch sheet on that one").
    sheetMark(p, k) {
      const held = this.settled[p];
      if (held) return natureKey(held) === k ? 'in' : 'out';
      return this.sheet[`${p}|${k}`] || null;
    },
    cycleMark(p, k) {
      if (this.settled[p]) return;
      const now = this.sheetMark(p, k);
      const next = now === null ? 'out' : now === 'out' ? 'in' : null;
      const sheet = { ...this.sheet };
      if (next) sheet[`${p}|${k}`] = next; else delete sheet[`${p}|${k}`];
      this.sheet = sheet;
      try { localStorage.setItem(this.sheetKey(), JSON.stringify(sheet)); } catch { /* storage off */ }
    },
    loadSheet() {
      try {
        let raw = localStorage.getItem(this.sheetKey());
        if (!raw) {
          raw = localStorage.getItem(this.oldSheetKey());
          if (raw) { localStorage.setItem(this.sheetKey(), raw); localStorage.removeItem(this.oldSheetKey()); }
        }
        this.sheet = JSON.parse(raw || '{}') || {};
      } catch { this.sheet = {}; }
    },
    shortNature(n) { return ASPECTS.map((a) => aspectWord(a, n[a])).join(' · '); },
    // Struck out for whichever row is in the first flask - the one the
    // player is thinking about.
    ruledOut(n) {
      const p = this.picked[0];
      return Boolean(p) && !this.possible.candidates[p].includes(natureKey(n));
    },
    reactionFor(id) { return describeReaction(this.reactions[id] || {}); },
    slotArt(i) { return this.picked[i] ? artForPrinciple(this.picked[i]) : FLASK_ART; },
    pick(p) {
      this.showResult = false;
      this.stones = null;
      this.picking = null;
      if (this.picked.includes(p)) { this.picked = this.picked.filter((x) => x !== p); return; }
      this.picked = [...this.picked, p].slice(-2);
    },
    unpick(i) { this.showResult = false; this.stones = null; this.picked = this.picked.filter((_, j) => j !== i); },
    // Why a side of a switch might already be the answer: "known" if the
    // world said so, "follows" if every fitting arrangement agrees.
    hintFor(p, a, value) {
      return this.lore.known?.[p]?.[a] === value ? 'known' : '';
    },
    paramsFor(id) {
      if (id !== 'hint') return {};
      const open = this.P.find((p) => ASPECTS.some((a) => this.lore.known?.[p]?.[a] === undefined));
      return { principle: open || this.P[0] };
    },
    priceOf(id) { return techPrice(this.civ, id); },
    doMix() {
      const [a, b] = this.picked;
      // Both flasks full, from this bench - a button left lit after the
      // flasks emptied (a stalled redraw on an iPad) mixed nothing and threw.
      if (!this.P.includes(a) || !this.P.includes(b) || !this.mixCheck.ok) return;
      const aimed = this.target;
      const was = { [a]: this.possible.candidates[a].length, [b]: this.possible.candidates[b].length };
      this.act('mix', { a, b, target: aimed || undefined });
      this.picked = [];
      // The aim stays for the next experiment - with a library that is
      // usually the same hunt - unless this one just learned it.
      this.$nextTick(() => {
        if (aimed && !this.reachable.some((t) => t.id === aimed)) this.target = '';
        // "Fire 8 -> 4": what the experiment actually bought you.
        const now = { [a]: this.possible.candidates[a].length, [b]: this.possible.candidates[b].length };
        const said = [a, b]
          .filter((p) => now[p] < was[p])
          .map((p) => `${PRINCIPLE_NAMES[p]} ${was[p]}→${now[p]}`);
        const last = this.experiments[this.experiments.length - 1];
        if (last && last.failed) {
          const need = describeReaction(this.reactions[last.target] || {});
          const counts = said.length ? ` · ${said.join(' · ')} natures left` : '';
          this.crossedOff = `So ${PRINCIPLE_NAMES[a]} and ${PRINCIPLE_NAMES[b]} are not both ${need}${counts}`;
        } else if (said.length) {
          this.crossedOff = `${said.join(' · ')} natures left`;
        } else {
          // Sharing nothing rules out no single nature - it ties the two
          // together, which is worth more.
          this.crossedOff = last && !this.sharedCount(last)
            ? `${PRINCIPLE_NAMES[a]} and ${PRINCIPLE_NAMES[b]} are exact opposites now`
            : '';
        }
      });
    },
    startTheory(p) {
      this.theoryOf = p;
      // Only what you were TOLD fills in. Pre-filling from the solver gave
      // the answer away (playtest iWlgN: "it has already highlighted cold,
      // wet and light even though I have not deduced that").
      const k = this.lore.known?.[p] || {};
      // ...and what YOU worked out: a row you have ringed (●) on your own
      // sheet fills the rest (Matt, playtest 9: "that should auto fill the
      // three things below like what I claim it is"). Nothing else is
      // chosen for you: a default of hot, wet and heavy looked like an
      // answer and got published as one (playtest b6IpO).
      const ringed = this.NATURE_KEYS.filter((key) => this.sheetMark(p, key) === 'in');
      const mine = ringed.length === 1 ? this.natureOfKey(ringed[0]) : {};
      this.theory = Object.fromEntries(ASPECTS.map((a) => [a, k[a] ?? mine[a] ?? null]));
    },
    close() { this.mode = null; this.theoryOf = null; },
    dismissReveal() {
      const r = this.learnt;
      if (!r) return;
      const entry = (this.g.log || [])[r.at - (this.g.logBase || 0)];
      const last = r.j >= (entry?.reveals?.length || 1) - 1;
      if (last) { this.revealedTo = r.at; this.revealedIn = 0; } else { this.revealedTo = r.at - 1; this.revealedIn = r.j + 1; }
      try { localStorage.setItem(`${REVEAL_KEY}:${this.g.seed}`, String(this.revealedTo)); } catch { /* storage off */ }
    },
    tap(a) {
      if (a.id === 'publish') {
        this.mode = 'publish';
        const first = this.picked.find((p) => !this.settled[p] && !this.published[p]);
        if (first) this.startTheory(first); else this.theoryOf = null;
        return;
      }
      if (a.id === 'hint') { this.mode = 'hint'; return; }
      if (a.id === 'fire') { this.machineAim = ''; this.mode = 'machines'; return; }
      this.act(a.id, {});
    },
    act(actionId, params = {}) {
      this.close();
      this.$emit('act', { role: 'science', actionId, params });
    },
  },
};
</script>
